/**
 * YearEndFooter
 *
 * 연말정산 페이지 하단 공유 컨트롤
 * - 링크 복사 버튼
 * - 조회수 표시
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  IconLink,
  IconEye,
  IconCheck,
  IconHome,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * @param {boolean} isMyPage - 내 페이지 여부
 * @param {string} yearEndId - yearEnd 문서 ID
 * @param {number} viewCount - 조회수
 */
export const YearEndFooter = ({
  isMyPage,
  yearEndId,
  viewCount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // 링크 복사
  const handleCopyLink = useCallback(async () => {
    const shareUrl = `${window.location.origin}/year-end/${yearEndId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setSnackbar({
        open: true,
        message: t('yearEnd.share.linkCopied'),
        severity: 'success',
      });
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setSnackbar({
        open: true,
        message: t('yearEnd.share.linkCopied'),
        severity: 'success',
      });
      setTimeout(() => setCopySuccess(false), 2000);
    }
  }, [yearEndId, t]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {/* 공유 컨트롤 - 내 페이지 + yearEndId 있을 때만 */}
      {isMyPage && yearEndId && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            maxWidth: 400,
            mx: 'auto',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {t('yearEnd.share.title')}
          </Typography>

          {/* 조회수 */}
          {typeof viewCount === 'number' && viewCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconEye size={16} />
              <Typography variant="caption" color="text.secondary">
                {viewCount}
              </Typography>
            </Box>
          )}

          {/* 링크 복사 버튼 */}
          <Button
            variant="outlined"
            fullWidth
            startIcon={copySuccess ? <IconCheck size={18} /> : <IconLink size={18} />}
            onClick={handleCopyLink}
          >
            {copySuccess ? t('yearEnd.share.copied') : t('yearEnd.share.copyLink')}
          </Button>

          <Typography variant="caption" color="text.secondary">
            {t('yearEnd.share.publicHint')}
          </Typography>

          {/* 마이 스레드 인사이트로 이동 버튼 */}
          <Button
            variant="contained"
            fullWidth
            startIcon={<IconHome size={18} />}
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 1 }}
          >
            {t('yearEnd.share.goToMyInsight', '마이 스레드 인사이트로 이동')}
          </Button>
        </Box>
      )}

      {/* 공유 페이지에서 CTA */}
      {!isMyPage && (
        <Button
          variant="contained"
          href="/year-end"
          sx={{ mt: 2 }}
        >
          {t('yearEnd.share.createYourOwn')}
        </Button>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={handleCloseSnackbar}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default YearEndFooter;
